import { useEffect, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, apiRequest } from '../../lib/api-client';
import { queryKeys } from '../../lib/api';
import { AuthLayout } from './auth-layout';

type Outcome = { status: 'verified' } | { status: 'failed'; message: string };

/**
 * One verification attempt per token, shared across component instances.
 *
 * A verification token is single-use, so the request must be issued exactly once no matter how
 * many times this component mounts -- and React StrictMode mounts, unmounts, then remounts in
 * development. Neither of the obvious guards survives that cycle:
 *
 * - A `useRef` flag stops the second attempt, but the first attempt's result is delivered to the
 *   unmounted instance, so the remounted one waits forever.
 * - TanStack Query's mutation state does not help either: the remount constructs a new observer,
 *   which has no knowledge of the request the previous instance started.
 *
 * Keying the in-flight promise by token fixes both halves at once. The first mount starts the
 * request; every later mount awaits the same promise and sees the same settled outcome. Rejections
 * are folded into the resolved value so a cached entry can never surface as an unhandled rejection.
 */
const attempts = new Map<string, Promise<Outcome>>();

function verifyOnce(token: string): Promise<Outcome> {
  const existing = attempts.get(token);
  if (existing !== undefined) return existing;

  const attempt = apiRequest<{ status: string }>('/auth/verify-email', {
    method: 'POST',
    body: { token },
  })
    .then((): Outcome => ({ status: 'verified' }))
    .catch((error: unknown): Outcome => ({
      status: 'failed',
      message:
        error instanceof ApiError ? error.message : 'This verification link could not be used.',
    }));

  attempts.set(token, attempt);
  return attempt;
}

/**
 * Consume a verification link.
 *
 * The token arrives in the query string, so verification runs on mount rather than behind a
 * button: the customer already expressed intent by following the link.
 */
export function VerifyEmailRoute() {
  const { token } = useSearch({ from: '/verify-email' });
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    if (token === undefined || token === '') return;

    let active = true;
    void verifyOnce(token).then((result) => {
      if (!active) return;
      setOutcome(result);
      if (result.status === 'verified') {
        // The session payload carries `emailVerified`, so it is stale once this succeeds.
        void queryClient.invalidateQueries({ queryKey: queryKeys.session });
      }
    });

    return () => {
      active = false;
    };
  }, [token, queryClient]);

  if (token === undefined || token === '') {
    return (
      <AuthLayout title="Verification link required">
        <p className="text-sm text-neutral-700">
          Open the link from your verification email to continue.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={
        outcome === null
          ? 'Verifying...'
          : outcome.status === 'verified'
            ? 'Email verified'
            : 'Verification failed'
      }
      footer={
        <Link to="/login" className="link">
          Continue to sign in
        </Link>
      }
    >
      {outcome === null && (
        <p className="text-sm text-neutral-600">Confirming your email address.</p>
      )}
      {outcome?.status === 'verified' && (
        <p className="text-sm text-neutral-700" data-testid="verify-success">
          Your email address is confirmed. You can sign in now.
        </p>
      )}
      {outcome?.status === 'failed' && (
        <p role="alert" className="text-sm text-red-800">
          {outcome.message}
        </p>
      )}
    </AuthLayout>
  );
}
